import React from 'react';
import {
    LuArrowDown,
    LuArrowUpRight,
    LuCheck,
    LuChevronDown,
    LuCopy,
    LuMaximize2,
    LuMic,
    LuMicOff,
    LuMinimize2,
    LuMinus,
    LuRotateCcw,
    LuSendHorizontal,
    LuShieldCheck,
    LuSquare,
    LuTriangleAlert,
    LuVolume2,
    LuX,
} from 'react-icons/lu';
import { RiRobot3Line } from 'react-icons/ri';
import { playGenie } from './genie';
import { SpeechRecognitionApi, canSpeak, recognitionErrorMessage, speak, stopSpeaking } from './speech';
import { createSessionId } from '../../Utils/SessionId';
import '../../Styles/AegisAssistWidget.css';

/*
 * Client-facing Aegis AI chat widget: a floating launcher plus a macOS-style chat window.
 * Self-contained on purpose (own `aaw-` scoped CSS, no Bootstrap, transport passed in) so the
 * same component runs in the builder's live trial and in the embeddable client script (src/embed).
 *
 * sendMessage(text, { onToken, onThinking, onDone, onError }, signal, { clientTime, sessionId }) streams one answer;
 * clientTime is the visitor's browser date/time so the assistant can answer "what time is it", and sessionId
 * names the visible conversation so follow-ups are understood. onSessionEnd(sessionId) fires when it is reset.
 * fallbackMessage is shown instead when no answer starts within RESPONSE_TIMEOUT_MS.
 */

const DEFAULT_COLOR = '#1c4fa6';
const TEXTAREA_MAX_HEIGHT = 120;
const TEASER_DELAY_MS = 1100;
const TEASER_HIDE_MS = 7000;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const RESPONSE_TIMEOUT_MS = 25000;
const DEFAULT_FALLBACK = 'Sorry, this is taking longer than expected. Please try again in a moment.';

const DEFAULT_STARTERS = [
    'What can you help me with?',
    'Summarize the most important information',
    'What are the key dates or deadlines?',
];

// The maximize fold animates this length. Registered from JS (not only @property in the CSS)
// because @property rules are ignored inside the embed script's shadow root.
if (typeof window !== 'undefined' && window.CSS?.registerProperty) {
    try {
        window.CSS.registerProperty({ name: '--aaw-fold', syntax: '<length>', inherits: true, initialValue: '0px' });
    } catch {
        // Already registered (another widget instance or the stylesheet).
    }
}

let widgetMessageCounter = 0;
const nextId = () => {
    widgetMessageCounter += 1;
    return `aaw-msg-${widgetMessageCounter}`;
};

const hexToRgb = (hex) => {
    const raw = String(hex || '').trim().replace('#', '');
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    if (!/^[0-9a-f]{6}$/i.test(full)) return hexToRgb(DEFAULT_COLOR);
    return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
};

const mix = (rgb, target, amount) => rgb.map((c, i) => Math.round(c + (target[i] - c) * amount));

const toCss = (rgb) => `rgb(${rgb.join(', ')})`;

// WCAG relative luminance, used to keep header/bubble text readable on light brand colours.
const luminance = (rgb) => {
    const [r, g, b] = rgb.map((c) => {
        const v = c / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const buildTheme = (color) => {
    const rgb = hexToRgb(color);
    return {
        '--aaw-primary': toCss(rgb),
        '--aaw-primary-rgb': rgb.join(', '),
        '--aaw-primary-deep': toCss(mix(rgb, [0, 0, 0], 0.32)),
        '--aaw-primary-bright': toCss(mix(rgb, [255, 255, 255], 0.18)),
        '--aaw-primary-tint': toCss(mix(rgb, [255, 255, 255], 0.92)),
        '--aaw-primary-ink': toCss(luminance(rgb) > 0.6 ? mix(rgb, [0, 0, 0], 0.55) : rgb),
        '--aaw-on-primary': luminance(rgb) > 0.45 ? '#10181c' : '#ffffff',
    };
};

const formatTime = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const browserDateTime = () => {
    const now = new Date();
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${now.toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}${zone ? ` (${zone})` : ''}`;
};

const PlainText = ({ content }) => <span className="aaw-plain">{content}</span>;

export default function AegisAssistWidget({
    title = 'AI Assistant',
    description = '',
    primaryColor = DEFAULT_COLOR,
    icon: Icon = RiRobot3Line,
    // The client's uploaded logo, shown inside the chat window; the preset icon stays on the floating launcher.
    logo: Logo = Icon,
    position = 'bottom-right',
    sendMessage,
    fallbackMessage,
    starterPrompts = DEFAULT_STARTERS,
    teaser,
    badge,
    renderContent: RenderContent = PlainText,
    poweredBy = 'Aegis Healthcare Solutions',
    exiting = false,
    onExited,
    onSessionEnd,
    zIndex = 1055,
}) {
    // The assistant's memory follows the visible conversation: a reset starts a new session.
    const sessionIdRef = React.useRef(createSessionId());
    // closed -> opening -> open -> closing -> closed. The genie effect runs on the transitions.
    const [phase, setPhase] = React.useState('closed');
    const [maximized, setMaximized] = React.useState(false);
    const [resizing, setResizing] = React.useState(false);
    const [messages, setMessages] = React.useState([]);
    const [input, setInput] = React.useState('');
    const [streaming, setStreaming] = React.useState(false);
    const [thinkingText, setThinkingText] = React.useState('');
    const [unread, setUnread] = React.useState(false);
    const [showTeaser, setShowTeaser] = React.useState(false);
    const [atBottom, setAtBottom] = React.useState(true);
    const [copiedId, setCopiedId] = React.useState(null);
    const [listening, setListening] = React.useState(false);
    const [speakingId, setSpeakingId] = React.useState(null);
    // Why voice input or read-aloud just failed, shown above the composer for a few seconds.
    const [voiceNotice, setVoiceNotice] = React.useState('');

    const abortRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const panelRef = React.useRef(null);
    const launcherRef = React.useRef(null);
    const genieCancelRef = React.useRef(null);
    // Hiding the window resets its scroll position; keep it so the window re-opens where it was.
    const savedScrollRef = React.useRef(0);
    const bodyRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const recognitionRef = React.useRef(null);
    const phaseRef = React.useRef(phase);
    phaseRef.current = phase;

    const theme = React.useMemo(() => buildTheme(primaryColor), [primaryColor]);
    const isOpen = phase === 'opening' || phase === 'open';
    const corner = ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(position) ? position : 'bottom-right';
    const teaserText = teaser ?? `Hi! Ask me anything about ${title}.`;

    // Abort any in-flight answer and animation when the widget goes away.
    React.useEffect(() => () => {
        abortRef.current?.abort();
        genieCancelRef.current?.();
        recognitionRef.current?.abort();
        stopSpeaking();
    }, []);

    // Restore: the window pours out of the launcher. Runs before paint so the real panel never flashes.
    React.useLayoutEffect(() => {
        if (phase !== 'opening') return undefined;
        if (bodyRef.current) bodyRef.current.scrollTop = savedScrollRef.current;
        const cancel = playGenie({
            panel: panelRef.current,
            target: launcherRef.current,
            container: rootRef.current,
            mode: 'restore',
            onDone: () => {
                setPhase('open');
                inputRef.current?.focus();
            },
        });
        genieCancelRef.current = cancel;
        return cancel;
    }, [phase]);

    // The wheel over the window scrolls the conversation only, never the host page - also on sites whose
    // smooth-scroll libraries take over wheel events at the window. Native listener: React's onWheel is passive.
    React.useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return undefined;
        const handleWheel = (event) => {
            if (event.ctrlKey) return; // pinch / browser zoom
            event.stopPropagation();
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return; // wide tables and code blocks scroll sideways
            event.preventDefault();
            const input = event.target.closest?.('.aaw-input');
            const scroller = input && input.scrollHeight > input.clientHeight ? input : bodyRef.current;
            if (!scroller) return;
            const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientHeight : 1;
            scroller.scrollTop += event.deltaY * unit;
        };
        panel.addEventListener('wheel', handleWheel, { passive: false });
        return () => panel.removeEventListener('wheel', handleWheel);
    }, []);

    React.useEffect(() => {
        const showTimer = setTimeout(() => setShowTeaser(true), TEASER_DELAY_MS);
        const hideTimer = setTimeout(() => setShowTeaser(false), TEASER_DELAY_MS + TEASER_HIDE_MS);
        return () => {
            clearTimeout(showTimer);
            clearTimeout(hideTimer);
        };
    }, []);

    // With reduced motion there is no launcher exit animation to wait for.
    React.useEffect(() => {
        if (!exiting) return undefined;
        abortRef.current?.abort();
        const reduced = window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
        const timer = setTimeout(() => onExited && onExited(), reduced ? 0 : 520);
        return () => clearTimeout(timer);
    }, [exiting, onExited]);

    // Grow the composer with its text. The closed window is display:none and measures 0px, so it is
    // (re)measured once the window opens instead of being stuck at a height too small for one line.
    React.useEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        if (!textarea.scrollHeight) return;
        textarea.style.height = `${Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
    }, [input, isOpen]);

    React.useEffect(() => {
        if (atBottom && bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, streaming, thinkingText, atBottom]);

    const openPanel = () => {
        if (phaseRef.current !== 'closed') return;
        setShowTeaser(false);
        setUnread(false);
        setPhase('opening');
    };

    // Voice input fills the composer as the visitor speaks; they review it and press send.
    const discardListening = () => {
        const recognition = recognitionRef.current;
        if (!recognition) return;
        recognition.onresult = null;
        recognition.abort();
    };

    const toggleListening = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            return;
        }
        const recognition = new SpeechRecognitionApi();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = navigator.language || 'en-US';
        const typed = input.trim() ? `${input.trim()} ` : '';
        recognition.onresult = (event) => {
            setInput(typed + Array.from(event.results).map((result) => result[0].transcript).join(''));
        };
        recognition.onerror = (event) => {
            if (event.error === 'aborted') return; // stopped by us (send, minimize, close)
            console.warn('[Aegis Assist] voice input error:', event.error);
            setVoiceNotice(recognitionErrorMessage(event.error));
        };
        recognition.onend = () => {
            recognitionRef.current = null;
            setListening(false);
        };
        recognitionRef.current = recognition;
        setVoiceNotice('');
        try {
            recognition.start();
            setListening(true);
        } catch (error) {
            recognitionRef.current = null;
            console.warn('[Aegis Assist] voice input could not start:', error);
            setVoiceNotice(recognitionErrorMessage());
        }
    };

    const toggleSpeaking = (message) => {
        if (speakingId === message.id) {
            stopSpeaking();
            setSpeakingId(null);
            return;
        }
        const done = () => setSpeakingId((current) => (current === message.id ? null : current));
        setSpeakingId(message.id);
        setVoiceNotice('');
        speak(message.text, done, () => {
            done();
            setVoiceNotice("This browser couldn't read the answer aloud. Check that the device volume is on and try again.");
        });
    };

    React.useEffect(() => {
        if (!voiceNotice) return undefined;
        const timer = setTimeout(() => setVoiceNotice(''), 6000);
        return () => clearTimeout(timer);
    }, [voiceNotice]);

    // Minimize: snapshot the window as it is right now and funnel it into the launcher.
    const minimizePanel = () => {
        if (phaseRef.current !== 'open') return;
        discardListening();
        savedScrollRef.current = bodyRef.current?.scrollTop || 0;
        genieCancelRef.current = playGenie({
            panel: panelRef.current,
            target: launcherRef.current,
            container: rootRef.current,
            mode: 'minimize',
            // Un-maximize only once the window is gone, so it never visibly shrinks mid-animation.
            onDone: () => {
                setPhase('closed');
                setMaximized(false);
            },
        });
        setPhase('closing');
        setResizing(false);
    };

    const togglePanel = () => {
        if (isOpen) minimizePanel();
        else openPanel();
    };

    const resetConversation = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        discardListening();
        stopSpeaking();
        setSpeakingId(null);
        setStreaming(false);
        setThinkingText('');
        if (messages.length) onSessionEnd?.(sessionIdRef.current);
        sessionIdRef.current = createSessionId();
        setMessages([]);
        setInput('');
        setAtBottom(true);
    };

    const closePanel = () => {
        resetConversation();
        minimizePanel();
    };

    // Maximize / restore with the page-fold animation. The class is dropped for two frames first
    // so a quick second toggle restarts the animation instead of being ignored.
    const changeMaximized = (next) => {
        setResizing(false);
        setMaximized((value) => (typeof next === 'function' ? next(value) : next));
        requestAnimationFrame(() => requestAnimationFrame(() => setResizing(true)));
    };

    const handlePanelAnimationEnd = (event) => {
        if (event.target === event.currentTarget && event.animationName === 'aaw-window-resize') {
            setResizing(false);
        }
    };

    const updateMessage = (id, patch) =>
        setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, ...(typeof patch === 'function' ? patch(msg) : patch) } : msg)));

    const ask = async (text, { appendUser = true } = {}) => {
        const query = String(text || '').trim();
        if (!query || streaming || !sendMessage) return;
        discardListening();

        const assistantId = nextId();
        setMessages((prev) => [
            ...prev,
            ...(appendUser ? [{ id: nextId(), role: 'user', text: query, time: new Date() }] : []),
            { id: assistantId, role: 'assistant', text: '', status: 'pending', query, time: new Date() },
        ]);
        setInput('');
        setThinkingText('');
        setStreaming(true);
        setAtBottom(true);

        const controller = new AbortController();
        abortRef.current = controller;
        let finished = false;
        let timeoutId;
        const finish = (patch) => {
            if (finished || controller.signal.aborted) return;
            finished = true;
            clearTimeout(timeoutId);
            updateMessage(assistantId, patch);
            setStreaming(false);
            setThinkingText('');
            abortRef.current = null;
            if (phaseRef.current !== 'open' && phaseRef.current !== 'opening') setUnread(true);
        };

        // No answer yet after the timeout: show the widget's fallback message and drop the request.
        timeoutId = setTimeout(() => {
            finish({ status: 'done', text: fallbackMessage?.trim() || DEFAULT_FALLBACK, time: new Date() });
            controller.abort();
        }, RESPONSE_TIMEOUT_MS);

        try {
            await sendMessage(query, {
                onThinking: (status) => {
                    if (status && !controller.signal.aborted) setThinkingText(status);
                },
                onToken: (chunk) => {
                    if (controller.signal.aborted) return;
                    clearTimeout(timeoutId);
                    updateMessage(assistantId, (msg) => ({ text: msg.text + chunk, status: 'streaming' }));
                },
                onDone: () => finish({ status: 'done', time: new Date() }),
                onError: (message) => finish({ status: 'error', error: message || 'Something went wrong. Please try again.' }),
            }, controller.signal, { clientTime: browserDateTime(), sessionId: sessionIdRef.current });
            // Transports that resolve without calling onDone still complete the answer.
            finish({ status: 'done', time: new Date() });
        } catch (error) {
            finish({ status: 'error', error: error?.message || 'Something went wrong. Please try again.' });
        }
    };

    const stopStreaming = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        setStreaming(false);
        setThinkingText('');
        setMessages((prev) => prev
            .filter((msg) => !(msg.status === 'pending'))
            .map((msg) => (msg.status === 'streaming' ? { ...msg, status: 'done', stopped: true } : msg)));
    };

    const retry = (message) => {
        setMessages((prev) => prev.filter((msg) => msg.id !== message.id));
        ask(message.query, { appendUser: false });
    };

    const copy = async (message) => {
        try {
            await navigator.clipboard.writeText(message.text);
            setCopiedId(message.id);
            setTimeout(() => setCopiedId((current) => (current === message.id ? null : current)), 1600);
        } catch (error) {
            console.error(error);
        }
    };

    const handleScroll = () => {
        const el = bodyRef.current;
        if (!el) return;
        setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
    };

    const scrollToBottom = () => {
        setAtBottom(true);
        bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            ask(input);
        }
    };

    const handlePanelKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            if (maximized) changeMaximized(false);
            else minimizePanel();
        }
    };

    const rootClass = [
        'aaw-root',
        `aaw-${corner}`,
        `aaw-phase-${phase}`,
        maximized ? 'is-maximized' : '',
        resizing ? 'is-resizing' : '',
        exiting ? 'is-exiting' : '',
        Logo.isLogo ? 'aaw-has-logo' : '',
    ].filter(Boolean).join(' ');

    return (
        <div ref={rootRef} className={rootClass} style={{ ...theme, zIndex }}>
            <div className="aaw-backdrop" onClick={() => changeMaximized(false)} aria-hidden="true" />

            <section
                ref={panelRef}
                className="aaw-panel"
                role="dialog"
                aria-label={title}
                aria-hidden={!isOpen}
                onAnimationEnd={handlePanelAnimationEnd}
                onKeyDown={handlePanelKeyDown}
            >
                <header className="aaw-head" onDoubleClick={(event) => {
                    if (!event.target.closest('button')) changeMaximized((value) => !value);
                }}>
                    <span className="aaw-head-glow aaw-head-glow-a" aria-hidden="true" />
                    <span className="aaw-head-glow aaw-head-glow-b" aria-hidden="true" />

                    <div className="aaw-lights">
                        <button type="button" className="aaw-light aaw-light-close" onClick={closePanel} aria-label="Close and clear chat" title="Close">
                            <LuX />
                        </button>
                        <button type="button" className="aaw-light aaw-light-min" onClick={minimizePanel} aria-label="Minimize" title="Minimize">
                            <LuMinus />
                        </button>
                        <button type="button" className="aaw-light aaw-light-max" onClick={() => changeMaximized((value) => !value)} aria-label={maximized ? 'Restore size' : 'Maximize'} title={maximized ? 'Restore' : 'Maximize'}>
                            {maximized ? <LuMinimize2 /> : <LuMaximize2 />}
                        </button>
                    </div>

                    <div className="aaw-head-brand">
                        <span className="aaw-head-avatar">
                            <Logo />
                        </span>
                        <span className="aaw-head-text">
                            <span className="aaw-head-title">{title}</span>
                            <span className="aaw-head-status" title={description || undefined}>
                                {description || 'Online'}
                            </span>
                        </span>
                    </div>

                    <div className="aaw-head-actions">
                        {badge ? <span className="aaw-badge">{badge}</span> : null}
                        <button
                            type="button"
                            className="aaw-icon-btn"
                            onClick={resetConversation}
                            disabled={messages.length === 0}
                            aria-label="Start a new conversation"
                            title="New conversation"
                        >
                            <LuRotateCcw />
                        </button>
                    </div>
                </header>

                <div className="aaw-body" ref={bodyRef} onScroll={handleScroll}>
                    {messages.length === 0 ? (
                        <div className="aaw-welcome">
                            <div className="aaw-orb">
                                <span className="aaw-orb-ring" aria-hidden="true" />
                                <span className="aaw-orb-core"><Logo /></span>
                            </div>
                            <h2 className="aaw-welcome-title">Hi there, how can I help?</h2>
                            <p className="aaw-welcome-text">
                                {`I'm ${title}. Ask me a question and I'll answer from our knowledge base.`}
                            </p>
                            <div className="aaw-starters">
                                {starterPrompts.map((prompt, index) => (
                                    <button
                                        key={prompt}
                                        type="button"
                                        className="aaw-starter"
                                        style={{ animationDelay: `${160 + index * 70}ms` }}
                                        onClick={() => ask(prompt)}
                                    >
                                        <span>{prompt}</span>
                                        <LuArrowUpRight className="aaw-starter-arrow" />
                                    </button>
                                ))}
                            </div>
                            <p className="aaw-grounded">
                                <LuShieldCheck /> Answers come only from this organization's knowledge base
                            </p>
                        </div>
                    ) : (
                        <div className="aaw-thread">
                            {messages.map((message) => {
                                if (message.role === 'user') {
                                    return (
                                        <div key={message.id} className="aaw-row is-user">
                                            <div className="aaw-bubble aaw-bubble-user">{message.text}</div>
                                            <div className="aaw-meta">
                                                <button type="button" className="aaw-copy" onClick={() => copy(message)} aria-label="Copy message">
                                                    {copiedId === message.id ? <><LuCheck /> Copied</> : <><LuCopy /> Copy</>}
                                                </button>
                                                <span>{formatTime(message.time)}</span>
                                            </div>
                                        </div>
                                    );
                                }

                                if (message.status === 'pending') {
                                    return (
                                        <div key={message.id} className="aaw-row is-assistant">
                                            <span className="aaw-avatar is-thinking"><Logo /></span>
                                            <div className="aaw-bubble aaw-bubble-bot aaw-typing">
                                                <span className="aaw-dots" aria-hidden="true"><i /><i /><i /></span>
                                                <span className="aaw-thinking">{thinkingText || 'Thinking…'}</span>
                                            </div>
                                        </div>
                                    );
                                }

                                if (message.status === 'error') {
                                    return (
                                        <div key={message.id} className="aaw-row is-assistant">
                                            <span className="aaw-avatar is-error"><LuTriangleAlert /></span>
                                            <div className="aaw-bubble aaw-bubble-error">
                                                <span>{message.error}</span>
                                                <button type="button" className="aaw-retry" onClick={() => retry(message)} disabled={streaming}>
                                                    <LuRotateCcw /> Try again
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }

                                const isStreamingMsg = message.status === 'streaming';
                                return (
                                    <div key={message.id} className="aaw-row is-assistant">
                                        <span className="aaw-avatar"><Logo /></span>
                                        <div className="aaw-msg">
                                            <div className={`aaw-bubble aaw-bubble-bot${isStreamingMsg ? ' is-streaming' : ''}`}>
                                                <RenderContent content={message.text} isStreaming={isStreamingMsg} />
                                            </div>
                                            {!isStreamingMsg ? (
                                                <div className="aaw-meta aaw-meta-bot">
                                                    <span>{formatTime(message.time)}</span>
                                                    {message.stopped ? <span>· stopped</span> : null}
                                                    <button type="button" className="aaw-copy" onClick={() => copy(message)} aria-label="Copy answer">
                                                        {copiedId === message.id ? <><LuCheck /> Copied</> : <><LuCopy /> Copy</>}
                                                    </button>
                                                    {canSpeak ? (
                                                        <button
                                                            type="button"
                                                            className={`aaw-copy${speakingId === message.id ? ' is-active' : ''}`}
                                                            onClick={() => toggleSpeaking(message)}
                                                            aria-label={speakingId === message.id ? 'Stop reading aloud' : 'Read answer aloud'}
                                                        >
                                                            {speakingId === message.id ? <><LuSquare /> Stop</> : <><LuVolume2 /> Listen</>}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <form className="aaw-composer" onSubmit={(event) => { event.preventDefault(); ask(input); }}>
                    <button
                        type="button"
                        className={`aaw-jump${atBottom ? '' : ' is-visible'}`}
                        onClick={scrollToBottom}
                        aria-label="Scroll to latest message"
                        tabIndex={atBottom ? -1 : 0}
                    >
                        <LuArrowDown />
                    </button>
                    {voiceNotice ? <div className="aaw-voice-notice" role="alert">{voiceNotice}</div> : null}
                    <div className="aaw-input-wrap">
                        <textarea
                            ref={inputRef}
                            className="aaw-input"
                            rows={1}
                            value={input}
                            placeholder={listening ? 'Listening…' : 'Type your question…'}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={handleKeyDown}
                            aria-label="Message"
                        />
                        {SpeechRecognitionApi ? (
                            <button
                                type="button"
                                className={`aaw-mic${listening ? ' is-listening' : ''}`}
                                onClick={toggleListening}
                                aria-label={listening ? 'Stop voice input' : 'Speak your question'}
                                aria-pressed={listening}
                                title={listening ? 'Stop listening' : 'Speak'}
                            >
                                {listening ? <LuMicOff /> : <LuMic />}
                            </button>
                        ) : null}
                        {streaming ? (
                            <button type="button" className="aaw-send is-stop" onClick={stopStreaming} aria-label="Stop generating" title="Stop">
                                <LuSquare />
                            </button>
                        ) : (
                            <button type="submit" className="aaw-send" disabled={!input.trim()} aria-label="Send message" title="Send">
                                <LuSendHorizontal />
                            </button>
                        )}
                    </div>
                    <div className="aaw-powered">
                        Powered by <strong>{poweredBy}</strong>
                    </div>
                </form>
            </section>

            <div className="aaw-launcher-wrap">
                <div className={`aaw-teaser${showTeaser && !isOpen && !exiting ? ' is-visible' : ''}`} role="status">
                    <button type="button" className="aaw-teaser-body" onClick={openPanel} tabIndex={showTeaser ? 0 : -1}>
                        {teaserText}
                    </button>
                    <button type="button" className="aaw-teaser-close" onClick={() => setShowTeaser(false)} aria-label="Dismiss" tabIndex={showTeaser ? 0 : -1}>
                        <LuX />
                    </button>
                </div>

                <span className="aaw-launcher-shadow" aria-hidden="true" />
                <button
                    ref={launcherRef}
                    type="button"
                    className="aaw-launcher"
                    onClick={togglePanel}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? `Minimize ${title}` : `Open ${title}`}
                >
                    <span className="aaw-ripple" aria-hidden="true" />
                    <span className="aaw-ripple aaw-ripple-2" aria-hidden="true" />
                    <span className="aaw-sheen" aria-hidden="true" />
                    <span className="aaw-launcher-icon aaw-launcher-icon-main"><Icon /></span>
                    <span className="aaw-launcher-icon aaw-launcher-icon-close"><LuChevronDown /></span>
                    {unread ? <span className="aaw-unread" aria-label="New reply" /> : null}
                </button>
            </div>
        </div>
    );
}
